import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, FormControlLabel, Grid, ListItem, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { ErrorOutline } from '@mui/icons-material';

import { FCheckbox, FHAutocomplete, FHRadio, FHRadioGroup, FHSelect } from '../../../../base';
import HypothesizedFunction from '../common/components/hypothesizedFunction';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter } from '../../../../../helpers';
import {
  FORTA_BLACK,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY2,
  FORTA_GRAY3,
  FORTA_ORANGE,
  FORTA_WHITE,
} from '../../../../../utils/colors';
import { Themes, BackgroundTypes } from '../../../../../constants';
import {
  GoalTypes,
  GOAL_DETAILS_FIELD_IDS,
  TreatmentPlanResourceVersion,
} from '../common/constants/general';
import { filterBehaviorsByGoalType } from '../common/utils/general';
import { useAutoSelectSingleFocusArea } from '../common/hooks/useAutoSelectSingleFocusArea';
import { useAutoSelectSingleGoalName } from '../common/hooks/useAutoSelectSingleGoalName';
import { getSingleFocusAreaBehaviorId, getSingleOptionId } from '../common/utils/autoSelect';
import { shouldUnlinkSAGGoal, willV2GoalBeActive } from './utils/general';

import type {
  IBehavior,
  IBehaviorGoalName,
  IOption,
  ITreatmentPlanGoal,
  ITreatmentPlanSymptom,
  SelectValueType,
} from '../../../../../utils/types';
import type { RootState } from '../../../../../store';
import type { ChangeEvent } from 'react';

interface GoalDetailsProps {
  goalIndex: number;
  goal: ITreatmentPlanGoal;
  treatmentPlanId: number;
  selectedGoalNameIds: number[];
  goalNames: IBehaviorGoalName[];
  errors: any;
  onUpdateField: (key: string, value: any) => void;
}

function GoalDetails(props: GoalDetailsProps) {
  const {
    goalIndex,
    goal,
    goalNames,
    errors,
    selectedGoalNameIds,
    treatmentPlanId,
    onUpdateField,
  } = props;
  const isTpEditable = useTreatmentPlanEditable();

  const { list: symptoms } = useSelector((state: RootState) => state.maladaptiveBehavior);
  const { list: behaviors, goalNames: behaviorGoalNamesMap } = useSelector(
    (state: RootState) => state.behaviors,
  );
  const { skillAcquisitionGoals } = useSelector((state: RootState) => state.goals);

  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [showUnlinkedGoalError, setShowUnlinkedGoalError] = useState<boolean>(false);
  const [showLinkedSAGError, setShowLinkedSAGError] = useState<boolean>(false);
  const [inactiveLinkedSAGIds, setInactiveLinkedSAGIds] = useState<number[]>([]);

  useEffect(() => {
    if (!goal) return;
    if (
      !Object.prototype.hasOwnProperty.call(goal, GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID) ||
      (!goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] &&
        goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID])
    ) {
      // Run onUpdateField for maladaptive_symptom_id after completion of
      // all other updates related to is_maladaptive_correction field
      setTimeout(() => {
        onUpdateField(GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID, null);
      }, 0);
    }
  }, [goal?.[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION]]);

  const singleGoalNameId = getSingleOptionId(goalNames);
  const { setUserClearedGoalName } = useAutoSelectSingleGoalName({
    singleGoalNameId,
    isEditing: Boolean(goal?.is_editing),
    currentGoalNameId: goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID],
    onUpdateField,
    goalNameFieldId: GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID,
  });

  useEffect(() => {
    if (!goal) return;
    // The TP must be editable, and the goal must be either new (no id) or editable.
    setIsEditable(isTpEditable);
    const isActive = willV2GoalBeActive(goal);
    if (
      isActive &&
      goal?.goal_type === GoalTypes.BehaviorReduction &&
      !goal?.linked_goals?.skill_acquisition?.length
    ) {
      setShowUnlinkedGoalError(true);
    } else {
      setShowUnlinkedGoalError(false);
    }

    const shouldUnlink = shouldUnlinkSAGGoal(goal);
    if (
      shouldUnlink &&
      goal?.goal_type === GoalTypes.BehaviorReduction &&
      goal?.linked_goals?.skill_acquisition?.length
    ) {
      setShowLinkedSAGError(true);
    } else {
      setShowLinkedSAGError(false);
    }

    const inactiveLinkedSAGs: number[] = [];
    if (goal?.goal_type === GoalTypes.BehaviorReduction) {
      goal?.linked_goals?.skill_acquisition?.forEach((g) => {
        const sagGoal = skillAcquisitionGoals.find((sg) => sg.id && sg.id === g.id);
        if (sagGoal) {
          const isSAGActive = willV2GoalBeActive(sagGoal);
          if (!isSAGActive) {
            if (sagGoal.id != null) inactiveLinkedSAGs.push(sagGoal.id);
          }
        }
      });
    }
    setInactiveLinkedSAGIds(inactiveLinkedSAGs);
  }, [isTpEditable, goal]);

  const filteredBehaviors = useMemo(() => {
    if (!behaviorGoalNamesMap) return behaviors;
    const filtered =
      behaviors?.filter((b) => {
        if (b?.id === goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id) {
          // Show focus area option, if already selected in goal
          return true;
        } else if (b?.id != null && b.id in behaviorGoalNamesMap) {
          // Goal names for the selected behaviors are loaded
          if (behaviorGoalNamesMap[b.id].length === 0) return false;
          const mappedGoalIds = behaviorGoalNamesMap[b.id].map((g) => g.id);
          const filteredMappedGoalIds = mappedGoalIds.filter(
            (id): id is number => id != null && !selectedGoalNameIds.includes(id),
          );
          return filteredMappedGoalIds.length > 0 ? true : false;
        } else {
          // Goal names for the selected behaviors are not loaded, allow the option to be selected.
          return true;
        }
      }) ?? [];
    return filterBehaviorsByGoalType(filtered, goal?.goal_type);
  }, [
    behaviorGoalNamesMap,
    selectedGoalNameIds,
    behaviors,
    goalNames,
    goal?.goal_type,
    goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id,
  ]);

  const linkableSAGoals = useMemo(() => {
    if (!skillAcquisitionGoals) return [];

    return skillAcquisitionGoals.filter((goal: ITreatmentPlanGoal) =>
      Object.prototype.hasOwnProperty.call(goal, 'id'),
    );
  }, [skillAcquisitionGoals]);

  const symptom = useMemo(() => {
    if (!symptoms || !goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]) return;

    const symptom = symptoms?.find(
      (s: ITreatmentPlanSymptom) => s.id === goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID],
    );

    return `${symptom?.label} - ${symptom?.associated_dsm5_symptom}`;
  }, [symptoms, goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]]);

  const handleMaladaptiveCorrectionUpdate = (value: string) => {
    if (value === 'Yes' && symptoms.length === 0) {
      toast.error('Treatment plan does not have a maladaptive behavior');
      onUpdateField(GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION, false);
      return;
    }
    onUpdateField(GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION, value === 'Yes' ? true : false);
  };

  const renderFocusAreaPlaceholder = () => <Typography>Select focus area</Typography>;

  const handleFocusAreaUpdate = (value: SelectValueType) => {
    onUpdateField(GOAL_DETAILS_FIELD_IDS.GOAL_NAME, {
      ...goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME],
      behavior: { id: value },
    });
  };

  const singleFocusAreaId = getSingleFocusAreaBehaviorId(filteredBehaviors);
  useAutoSelectSingleFocusArea({
    singleFocusAreaId,
    isEditing: Boolean(goal?.is_editing),
    currentFocusAreaId: goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id,
    onSelectFocusArea: handleFocusAreaUpdate,
  });

  const isOptionEqualToGoalValue = (option: IOption, value: IOption | null) =>
    option.id === value?.id;

  const renderGoalOption = (props: React.HTMLAttributes<HTMLLIElement>, option: IOption) => (
    <li {...props} key={option.id}>
      {option.label}
    </li>
  );

  const handleGoalChange = (_event: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
    if (newValue == null) {
      setUserClearedGoalName();
    }
    const id = newValue?.id;
    onUpdateField(
      GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID,
      id ?? null,
    );
  };

  const handleMaladaptiveRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleMaladaptiveCorrectionUpdate(e.target.value);
  };

  const renderDSM5SymptomPlaceholder = () => <Typography>Select DSM5 symptom</Typography>;

  const handleDSM5SymptomUpdate = (value: SelectValueType) => {
    setTimeout(() => {
      onUpdateField(GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID, value);
    });
  };

  const handleLinkedSAGChange = (e: ChangeEvent<HTMLInputElement>, saGoal: ITreatmentPlanGoal) => {
    const isChecked = e.target.checked;
    const currentLinkedGoals = goal?.linked_goals?.skill_acquisition || [];
    const updatedGoals = isChecked
      ? [
          ...currentLinkedGoals,
          {
            id: saGoal.id,
            goal_name: saGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME],
          },
        ]
      : currentLinkedGoals.filter((linkedGoal) => linkedGoal?.id !== saGoal.id);
    onUpdateField(GOAL_DETAILS_FIELD_IDS.LINKED_SKILL_ACQUISITION_GOALS, {
      ...goal.linked_goals,
      skill_acquisition: updatedGoals,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
      <Grid container spacing={3}>
        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Focus area*
          </Typography>
          {goal?.is_editing ? (
            <FHSelect
              data-testid="focusAreaDropDown"
              inputHeight={40}
              colorTheme={Themes.Primary}
              backgroundType={BackgroundTypes.Colored}
              displayEmpty
              disabled={!isEditable}
              selectFirstOption={false}
              renderValue={
                !goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id
                  ? renderFocusAreaPlaceholder
                  : undefined
              }
              options={[...(filteredBehaviors ?? [])]
                .filter((b): b is IBehavior & { id: number } => b?.id !== undefined)
                .sort((behaviorA, behaviorB) =>
                  behaviorA?.label?.toLowerCase().localeCompare(behaviorB?.label?.toLowerCase()),
                )
                .map((behavior) => ({
                  value: behavior.id,
                  label: capitalizeFirstLetter(behavior.label),
                }))}
              optionDataTestIdPrefix="focusAreaOption"
              value={goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id ?? ''}
              onUpdate={handleFocusAreaUpdate}
              error={errors && errors[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]}
            />
          ) : (
            <Typography align="left">
              {capitalizeFirstLetter(goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.label ?? '')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Prefix
          </Typography>
          <Typography
            data-testid={capitalizeFirstLetter(
              goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.prefix ?? '',
            ).replace(/\s+/g, '_')}
            align="left"
          >
            {capitalizeFirstLetter(goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.prefix ?? '')}
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Goal*
          </Typography>
          {goal?.is_editing ? (
            <FHAutocomplete
              data-testid="goalNameDropDown"
              inputHeight={40}
              fullWidth
              colorTheme={Themes.Primary}
              coloredBackground
              placeholder="Select goal"
              disabled={!isEditable}
              isOptionEqualToValue={isOptionEqualToGoalValue}
              options={
                goalNames
                  ?.sort((goalA, goalB) =>
                    goalA?.label?.toLowerCase().localeCompare(goalB?.label?.toLowerCase()),
                  )
                  .map((goalName: IBehaviorGoalName) => ({
                    id: goalName.id,
                    label: capitalizeFirstLetter(goalName.label),
                  })) ?? []
              }
              renderOption={renderGoalOption}
              value={
                goalNames &&
                goalNames.length > 0 &&
                goal &&
                goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]
                  ? {
                      id: goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID],
                      label: goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label,
                    }
                  : null
              }
              onChange={handleGoalChange}
              error={errors ? Boolean(errors[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]) : false}
            />
          ) : (
            <Typography align="left">
              {capitalizeFirstLetter(goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ?? '')}
            </Typography>
          )}
        </Grid>
        {goal?.goal_type === GoalTypes.BehaviorReduction && (
          <Grid item xs={12}>
            <HypothesizedFunction
              goalIndex={goalIndex}
              treatmentPlanId={treatmentPlanId}
              goalId={goal.id}
              version={TreatmentPlanResourceVersion.V2}
              hypothesizedFunctions={goal.hypothesized_functions ?? []}
              isEditable={isEditable && Boolean(goal?.is_editing)}
            />
          </Grid>
        )}
        {goal?.goal_type === GoalTypes.SkillAcquisition && (
          <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography fontWeight={'bold'} align="left" mb={1}>
              Linked behavior reduction goal(s)
            </Typography>
            {goal.linked_goals?.behavior_reduction &&
              goal.linked_goals?.behavior_reduction?.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${FORTA_GRAY2}`,
                    backgroundColor: FORTA_WHITE,
                    borderRadius: '10px',
                    padding: 0,
                    gap: 0,
                  }}
                >
                  {goal.linked_goals?.behavior_reduction?.map(
                    (linkedBehaviorReductionGoal, index, array) => (
                      <ListItem
                        data-testid={(
                          linkedBehaviorReductionGoal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ||
                          ''
                        ).replace(/\s+/g, '_')}
                        key={linkedBehaviorReductionGoal.id}
                        sx={{
                          p: 1.2,
                          borderBottom:
                            index === array.length - 1 ? 'none' : `1px solid ${FORTA_GRAY2}`,
                        }}
                      >
                        {capitalizeFirstLetter(
                          linkedBehaviorReductionGoal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ||
                            '',
                        )}
                      </ListItem>
                    ),
                  )}
                </Box>
              )}
          </Grid>
        )}
        {goal?.goal_type === GoalTypes.BehaviorReduction && (
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              fontWeight={'bold'}
              align="left"
              mb={1}
              color={
                errors && errors[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION]
                  ? FORTA_ERROR
                  : FORTA_BLACK
              }
            >
              Is a maladaptive behavior related to this goal?*
            </Typography>
            {goal?.is_editing ? (
              <FHRadioGroup
                data-testid="maladaptiveRadioGroup"
                rowGap="16px"
                colorTheme={Themes.Primary}
                coloredBackground
                defaultValue={'No'}
                value={goal?.[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] ? 'Yes' : 'No'}
                onChange={handleMaladaptiveRadioChange}
              >
                <FormControlLabel
                  data-testid="maladaptiveYesChoice"
                  sx={{ width: 'fit-content' }}
                  value="Yes"
                  control={<FHRadio colorTheme={Themes.Primary} />}
                  label="Yes"
                  disabled={!isEditable}
                />
                <FormControlLabel
                  data-testid="maladaptiveNoChoice"
                  sx={{ width: 'fit-content' }}
                  value="No"
                  control={<FHRadio colorTheme={Themes.Primary} />}
                  label="No"
                  disabled={!isEditable}
                />
              </FHRadioGroup>
            ) : (
              <Typography align="left">
                {goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] ? 'Yes' : 'No'}
              </Typography>
            )}
          </Grid>
        )}
        {symptoms.length > 0 && goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] && (
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography fontWeight={'bold'} align="left" mb={1}>
              If yes, please include DSM5 symptom*
            </Typography>
            {goal?.is_editing ? (
              <FHSelect
                data-testid="dSM5SymptomDropDown"
                sx={{ flexGrow: 1, width: 'auto' }}
                inputHeight={40}
                colorTheme={Themes.Primary}
                backgroundType={BackgroundTypes.EvenColored}
                displayEmpty
                disabled={!isEditable}
                renderValue={
                  !goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]
                    ? renderDSM5SymptomPlaceholder
                    : undefined
                }
                options={symptoms.map((symptom: ITreatmentPlanSymptom) => ({
                  value: symptom.id ?? '',
                  label: `${symptom.label} - ${symptom.associated_dsm5_symptom}`,
                }))}
                optionDataTestIdPrefix="dSM5SymptomOption"
                value={goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID] || ''}
                onUpdate={handleDSM5SymptomUpdate}
                error={errors && errors[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]}
              />
            ) : (
              <Typography align="left">{symptom}</Typography>
            )}
          </Grid>
        )}
        {goal?.goal_type === GoalTypes.BehaviorReduction && (
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              fontWeight={'bold'}
              color={
                showUnlinkedGoalError || inactiveLinkedSAGIds.length > 0 || showLinkedSAGError
                  ? FORTA_ERROR
                  : FORTA_BLACK
              }
              align="left"
              mb={1}
            >
              Linked skill acquisition goal (s)*
            </Typography>
            {showLinkedSAGError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                <ErrorOutline sx={{ fill: FORTA_ERROR }} />
                <Typography align="left" color={FORTA_ERROR}>
                  This goal will be rendered inactive with a linked skill acquisition goal. Please
                  add at least one active target or un-link all skill acquisition goal.
                </Typography>
              </Box>
            )}
            {showUnlinkedGoalError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                <ErrorOutline sx={{ fill: FORTA_ERROR }} />
                <Typography align="left" color={FORTA_ERROR}>
                  This goal will be rendered active without a linked skill acquisition goal. Please
                  link at least one active skill acquisition goal.
                </Typography>
              </Box>
            )}
            <Grid container>
              <Grid item xs={6}>
                {goal?.is_editing ? (
                  linkableSAGoals.length > 0 ? (
                    linkableSAGoals.map((saGoal: ITreatmentPlanGoal) => (
                      <Box
                        key={saGoal.id}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          backgroundColor: FORTA_BLUE.light3,
                          p: 1,
                          mt: 0.5,
                          borderRadius: 2,
                        }}
                      >
                        {saGoal.id != null && inactiveLinkedSAGIds.includes(saGoal.id) && (
                          <Typography textAlign="left" sx={{ color: FORTA_ERROR }}>
                            * This goal will be deactivated once the TP is signed. Please unlink it.
                          </Typography>
                        )}

                        <Box
                          key={saGoal.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            p: 1.5,
                            pl: 2,
                            borderRadius: 1,
                            border:
                              showLinkedSAGError ||
                              saGoal.id != null && inactiveLinkedSAGIds.includes(saGoal.id)
                                ? `1px solid ${FORTA_ERROR}`
                                : 'none',
                          }}
                        >
                          <FCheckbox
                            data-testid={(
                              saGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || ''
                            ).replace(/\s+/g, '_')}
                            colorTheme={Themes.Primary}
                            coloredBackground
                            checked={goal?.linked_goals?.skill_acquisition?.some(
                              (lg) => lg?.id === saGoal.id,
                            )}
                            iconStyles={{
                              boxShadow:
                                goal?.is_editing && isEditable
                                  ? '0px 4px 4px rgba(0, 0, 0, 0.25)'
                                  : 'none',
                              width: '25px',
                              height: '25px',
                              border: `1px solid ${FORTA_GRAY2}`,
                            }}
                            sx={{
                              cursor: `${
                                goal?.is_editing && isEditable ? 'pointer' : 'not-allowed'
                              } !important`,
                            }}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              handleLinkedSAGChange(e, saGoal)
                            }
                          />
                          <Typography>
                            {capitalizeFirstLetter(
                              saGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || '',
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ErrorOutline sx={{ fill: FORTA_ERROR }} />
                      <Typography align="left" color={FORTA_ERROR}>
                        No linkable skill acquisition goals found
                      </Typography>
                    </Box>
                  )
                ) : goal?.linked_goals?.skill_acquisition?.length &&
                  goal?.linked_goals?.skill_acquisition?.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      backgroundColor: FORTA_BLUE.light3,
                      p: 1,
                      borderRadius: 2,
                    }}
                  >
                    {goal?.linked_goals?.skill_acquisition?.map((linkedGoal) => (
                      <Box
                        key={linkedGoal.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'white',
                          p: 1.5,
                          pl: 2,
                          borderRadius: 1,
                          border:
                            showLinkedSAGError || inactiveLinkedSAGIds.includes(linkedGoal?.id)
                              ? `1px solid ${FORTA_ERROR}`
                              : 'none',
                        }}
                      >
                        <FCheckbox
                          data-testid={(
                            goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || ''
                          ).replace(/\s+/g, '_')}
                          colorTheme={Themes.Primary}
                          coloredBackground
                          checked={goal?.linked_goals?.skill_acquisition?.some(
                            (lg) => lg?.id === linkedGoal.id,
                          )}
                          iconStyles={{
                            boxShadow: 'none',
                            width: '25px',
                            height: '25px',
                            border: `1px solid ${FORTA_GRAY3}`,
                          }}
                          disabled={true}
                          sx={{
                            cursor: 'not-allowed !important',
                            '& .MuiCheckbox-root': {
                              cursor: 'not-allowed !important',
                            },
                          }}
                        />
                        <Typography>
                          {capitalizeFirstLetter(
                            linkedGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || '',
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : showUnlinkedGoalError ? null : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutline sx={{ fill: FORTA_ORANGE }} />
                    <Typography align="left" color={FORTA_ORANGE}>
                      No skill acquisition goals are linked to this goal
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default GoalDetails;
